import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEFAULT_TOASTER_LIFE, IWizardContext, ModalBuilder, WizardStepComponent, ZendraLocalizationServices, ZnButton, createFormModalConfigExtraLarge, ZnPill } from '@zendra/shared';
import type { StandPartitionStandDetailDto } from 'orchestrator';

import { AttributeInheritanceModal } from './attribute-inheritance-modal';
import type {
  AttributeInheritanceFormValue,
  AttributeInheritanceOriginalSummary,
  AttributeInheritanceRow,
  StepDefineAttributeInheritanceData,
} from './step-define-attribute-inheritance.models';
import type { IStepCreateAreaMaintenancesData } from '../interfaces/IStepCreateAreaMaintenancesData';
import type { IStepDefineResultingStandsData } from '../interfaces/IStepDefineResultingStandsData';
import type { IStepSelectStandToDivideData } from '../interfaces/IStepSelectStandToDivideData';

const EMPTY_VALUE = '-----';

const ATTRIBUTE_INHERITANCE_MODAL_CONFIG = createFormModalConfigExtraLarge({
  component: AttributeInheritanceModal,
  title: 'Geographic::StandDivision.StepDefineAttributeInheritance.Modal.Title',
});

@Component({
  selector: 'lib-step-define-attribute-inheritance',
  standalone: true,
  imports: [LocalizationPipe, ZnButton, ZnPill],
  templateUrl: './step-define-attribute-inheritance.html',
})
export class StepDefineAttributeInheritance
  implements WizardStepComponent<StepDefineAttributeInheritanceData>
{
  private readonly toaster = inject(ToasterService);
  private readonly modalBuilder = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly localizationService = inject(LocalizationService);

  readonly localizationKeyPrefix = 'Geographic::StandDivision.StepDefineAttributeInheritance';
  readonly previousStepId = 2;
  readonly areaMaintenanceStepId = 3;

  readonly originalStandId = signal<string | null>(null);
  readonly originalStandName = signal('');
  readonly originalStandArea = signal<number | null>(null);
  readonly originalSummary = signal<AttributeInheritanceOriginalSummary | null>(null);
  readonly rows = signal<AttributeInheritanceRow[]>([]);
  readonly sourceConfiguration = signal<AttributeInheritanceFormValue | null>(null);
  readonly sourceSignature = signal('');

  readonly allRowsConfigured = computed(
    () => this.rows().length > 0 && this.rows().every(row => !!row.configuration),
  );

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const selectedStandData =
      context.getStepState<IStepSelectStandToDivideData>(1)?.data;
    const previousStepData =
      context.getStepState<IStepDefineResultingStandsData>(this.previousStepId)?.data;
    const areaMaintenanceData =
      context.getStepState<IStepCreateAreaMaintenancesData>(this.areaMaintenanceStepId)?.data;
    const currentState =
      context.getStepState<StepDefineAttributeInheritanceData>(context.activeStepId())?.data;
    const sourceSignature = this.buildSourceSignature(
      selectedStandData,
      previousStepData,
      areaMaintenanceData,
    );
    const canRestore = !!currentState && currentState.sourceSignature === sourceSignature;

    const selectedStandId =
      selectedStandData?.standId !== null && selectedStandData?.standId !== undefined
        ? String(selectedStandData.standId)
        : null;
    const originalStandId =
      previousStepData?.originalStandId ?? currentState?.originalStandId ?? selectedStandId ?? null;
    const originalStandName =
      previousStepData?.originalStandName ??
      currentState?.originalStandName ??
      selectedStandData?.standName ??
      originalStandId ??
      '';
    const originalStandArea =
      previousStepData?.originalStandArea ??
      currentState?.originalStandArea ??
      selectedStandData?.standArea ??
      null;
    const sourceRows =
      previousStepData?.resultingStands ??
      currentState?.resultingStands.map(row => ({
        id: row.id,
        name: row.name,
        area: row.area,
      })) ??
      [];

    const savedRows = new Map(
      (canRestore ? currentState.resultingStands : []).map(row => [row.id, row.configuration]),
    );

    if (!canRestore && currentState) {
      context.patchStepState(context.activeStepId(), {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    this.sourceSignature.set(sourceSignature);
    this.originalStandId.set(originalStandId);
    this.originalStandName.set(originalStandName);
    this.originalStandArea.set(originalStandArea);
    this.originalSummary.set(
      canRestore
        ? currentState.originalSummary
        : this.buildOriginalSummary(selectedStandData?.standDetail),
    );
    this.rows.set(
      sourceRows.map(row => ({
        id: row.id,
        name: row.name,
        area: row.area,
        configuration: savedRows.get(row.id) ?? null,
      })),
    );
    this.sourceConfiguration.set(this.buildSourceConfiguration(selectedStandData?.standDetail));
  }

  canLeave(): boolean {
    if (this.allRowsConfigured()) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivision.StepDefineAttributeInheritance.RequiredConfiguration`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): StepDefineAttributeInheritanceData {
    const data: StepDefineAttributeInheritanceData = {
      sourceSignature: this.sourceSignature(),
      originalStandId: this.originalStandId(),
      originalStandName: this.originalStandName(),
      originalStandArea: this.originalStandArea(),
      originalSummary: this.originalSummary(),
      resultingStands: this.rows(),
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: this.allRowsConfigured(),
      completed: true,
      data,
    });
    return data;
  }

  openConfiguration(row: AttributeInheritanceRow): void {
    const modalRef = this.modalBuilder.openModal(AttributeInheritanceModal, {
      ...ATTRIBUTE_INHERITANCE_MODAL_CONFIG,
      subTitle: row.name,
      inputs: {
        standName: row.name,
        initialValue: row.configuration ?? this.sourceConfiguration(),
      },
    });

    modalRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (!result) {
        return;
      }

      this.rows.update(currentRows =>
        currentRows.map(currentRow =>
          currentRow.id === row.id
            ? { ...currentRow, configuration: result as AttributeInheritanceRow['configuration'] }
            : currentRow,
        ),
      );
    });
  }

  actionLabelKey(row: AttributeInheritanceRow): string {
    return `${this.localizationKeyPrefix}.Actions.${row.configuration ? 'Edit' : 'Configure'}`;
  }

  actionType(row: AttributeInheritanceRow): 'primary' | 'secondary' {
    return row.configuration ? 'secondary' : 'primary';
  }

  displayValue(value: string | null | undefined): string {
    return value?.trim() ? value : EMPTY_VALUE;
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return value.toFixed(2).replace('.', ',');
  }

  private buildOriginalSummary(
    standDetail?: StandPartitionStandDetailDto | null,
  ): AttributeInheritanceOriginalSummary | null {
    if (!standDetail) {
      return null;
    }

    const ownPlantation = standDetail.plantation?.isOwnPlantation;
    let ownPlantationValue = EMPTY_VALUE;
    if (ownPlantation !== null && ownPlantation !== undefined) {
      const ownPlantationLabelKey = ownPlantation ? 'AbpUi::Yes' : 'AbpUi::No';
      ownPlantationValue = this.localizationService.instant(ownPlantationLabelKey);
    }

    return {
      establishment: this.displayValue(standDetail.establishmentName),
      forestSituation: this.displayValue(standDetail.forestrySituationName),
      cycle:
        standDetail.cycleValue === null || standDetail.cycleValue === undefined
          ? EMPTY_VALUE
          : String(standDetail.cycleValue),
      propertyType: this.displayValue(standDetail.cycleRotation?.propertyTypeName),
      landContract: this.displayValue(standDetail.cycleRotation?.landContractNumber),
      forestContract: this.displayValue(standDetail.cycleRotation?.fomentContractNumber),
      spacing: this.displayValue(standDetail.plantation?.spacingName),
      ownPlantation: ownPlantationValue,
    };
  }

  private buildSourceConfiguration(
    standDetail: StandPartitionStandDetailDto | null | undefined,
  ): AttributeInheritanceFormValue | null {
    if (!standDetail) {
      return null;
    }

    const hasNearbyPopulation =
      standDetail.hasNearbyPopulation === null ||
      standDetail.hasNearbyPopulation === undefined
        ? null
        : String(standDetail.hasNearbyPopulation);
    let nearbyPopulationLabel = '';
    if (hasNearbyPopulation !== null) {
      const nearbyPopulationLabelKey = standDetail.hasNearbyPopulation
        ? 'LanguageService::Option:Yes'
        : 'LanguageService::Option:No';
      nearbyPopulationLabel =
        this.localizationService.instant(nearbyPopulationLabelKey);
    }

    return {
      standTypeId: this.normalizeId(standDetail.standTypeId),
      standTypeLabel: standDetail.standTypeName?.trim() ?? '',
      nearbyPopulation: hasNearbyPopulation,
      nearbyPopulationLabel,
      rotation:
        standDetail.rotationValue === null || standDetail.rotationValue === undefined
          ? ''
          : String(standDetail.rotationValue),
      plantingDate: this.formatSourceDate(standDetail.plantation?.plantationDate),
      plantingTypeId: this.normalizeId(standDetail.plantation?.plantationTypeId),
      plantingTypeLabel: standDetail.plantation?.plantationTypeName?.trim() ?? '',
      geneticMaterialId: this.normalizeId(standDetail.plantation?.geneticMaterialId),
      geneticMaterialLabel: standDetail.plantation?.geneticMaterialName?.trim() ?? '',
      speciesId: this.normalizeId(standDetail.plantation?.speciesId),
      speciesLabel: standDetail.plantation?.speciesName?.trim() ?? '',
      season: null,
      seasonLabel: '',
      contractor: null,
      contractorLabel: '',
      standState: null,
      standStateLabel: '',
    };
  }

  private buildSourceSignature(
    selectedStandData: IStepSelectStandToDivideData | undefined,
    resultingStandsData: IStepDefineResultingStandsData | undefined,
    areaMaintenanceData: IStepCreateAreaMaintenancesData | undefined,
  ): string {
    return JSON.stringify({
      selectedStandData: selectedStandData ?? null,
      resultingStandsData: resultingStandsData ?? null,
      areaMaintenanceData: areaMaintenanceData ?? null,
    });
  }

  private normalizeId(value: number | null | undefined): string | null {
    return value === null || value === undefined ? null : String(value);
  }

  private formatSourceDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const datePart = value.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return year && month && day ? `${day}/${month}/${year}` : '';
  }
}
